/**
 * Manufacturers — мини-справочник «Производители техники» (AK-235).
 *
 * Производитель = code (latin) + name (отображаемое) + country_code. Не
 * использует name_translations (бренд — имя собственное, не переводится), поэтому
 * это кастомная inline-таблица в стиле ReferenceMiniCatalog, а не сам generic
 * компонент. DELETE-guard через usage_count (модели техники).
 */
import { useState } from 'react';
import { Check, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  useCreateManufacturer,
  useDeleteManufacturer,
  useManufacturers,
  useUpdateManufacturer,
} from './hooks/useMachineryQueries';
import type { Manufacturer, ManufacturerPayload } from './machineryCatalogTypes';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';

interface Draft {
  code: string;
  name: string;
  country_code: string;
}

const emptyDraft = (): Draft => ({ code: '', name: '', country_code: '' });

function draftToPayload(d: Draft): ManufacturerPayload {
  return {
    code: d.code.trim(),
    name: d.name.trim(),
    country_code: d.country_code.trim() || null,
  };
}

const draftValid = (d: Draft): boolean =>
  /^[a-z0-9_]+$/.test(d.code.trim()) && d.name.trim().length > 0;

export function PlatformAdminManufacturersPage() {
  const q = useManufacturers();
  const createMut = useCreateManufacturer();
  const updateMut = useUpdateManufacturer();
  const deleteMut = useDeleteManufacturer();

  const items = q.data?.items ?? [];
  const [creating, setCreating] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft());

  const startCreate = () => setCreating(emptyDraft());
  const startEdit = (m: Manufacturer) => {
    setEditingId(m.id);
    setEditDraft({ code: m.code, name: m.name, country_code: m.country_code ?? '' });
  };

  const saveCreate = async () => {
    if (!creating || !draftValid(creating)) return;
    // AK-574: отказ сервера показываем, а не роняем в unhandled rejection.
    try {
      await createMut.mutateAsync(draftToPayload(creating));
      toast.success('Производитель добавлен');
      setCreating(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const saveEdit = async () => {
    if (!editingId || !draftValid(editDraft)) return;
    try {
      await updateMut.mutateAsync({ id: editingId, payload: draftToPayload(editDraft) });
      toast.success('Производитель обновлён');
      setEditingId(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const handleDelete = async (m: Manufacturer) => {
    if (m.usage_count > 0) return;
    if (!window.confirm(`Удалить «${m.name}»?`)) return;
    try {
      await deleteMut.mutateAsync(m.id);
      toast.success('Производитель удалён');
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <PlatformAdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Производители</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Мини-справочник брендов техники · {items.length} записей
          </p>
        </div>
        <Button onClick={startCreate} disabled={Boolean(creating)}>
          <Plus className="h-4 w-4" />
          Добавить производителя
        </Button>
      </div>

      <Card className="mt-5">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: '180px' }}>Код</TableHead>
                <TableHead>Название</TableHead>
                <TableHead style={{ width: '120px' }}>Страна</TableHead>
                <TableHead style={{ width: '150px' }}>Использование</TableHead>
                <TableHead style={{ width: '110px' }} className="text-right">
                  Действия
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {q.isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {creating && (
                <TableRow>
                  <TableCell>
                    <Input
                      value={creating.code}
                      onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                      placeholder="rostselmash"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.name}
                      onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                      placeholder="Ростсельмаш"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.country_code}
                      onChange={(e) =>
                        setCreating({ ...creating, country_code: e.target.value.toUpperCase() })
                      }
                      placeholder="RU"
                      maxLength={2}
                    />
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        onClick={() => void saveCreate()}
                        disabled={!draftValid(creating)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setCreating(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {items.map((m) =>
                editingId === m.id ? (
                  <TableRow key={m.id}>
                    <TableCell>
                      <Input
                        value={editDraft.code}
                        onChange={(e) => setEditDraft({ ...editDraft, code: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editDraft.name}
                        onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editDraft.country_code}
                        onChange={(e) =>
                          setEditDraft({ ...editDraft, country_code: e.target.value.toUpperCase() })
                        }
                        maxLength={2}
                      />
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => void saveEdit()}
                          disabled={!draftValid(editDraft)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={m.id}>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{m.code}</code>
                    </TableCell>
                    <TableCell className="font-medium text-foreground">{m.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.country_code ?? '—'}
                    </TableCell>
                    <TableCell>
                      {m.usage_count > 0 ? (
                        <Badge variant="secondary">{m.usage_count} моделей</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(m)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {m.usage_count > 0 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            title="Используется моделями техники — нельзя удалить"
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void handleDelete(m)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
      <p className="mt-3 text-[11px] text-muted-foreground">
        Производитель — имя собственное, не переводится. Удаление заблокировано,
        пока на бренд ссылается хотя бы одна модель техники.
      </p>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminManufacturersPage;
